import * as XLSX from 'xlsx';

/**
 * Export data to Excel
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions { header: 'Name', key: 'name' }
 * @param {string} fileName - Name of the file to save
 */
export const exportToExcel = (data, columns, fileName = 'report') => {
  const worksheetData = flattenDataForExport(data, columns);
  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
  XLSX.writeFile(workbook, `${fileName}_${new Date().getTime()}.xlsx`);
};

/**
 * Export data to CSV
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions { header: 'Name', key: 'name' }
 * @param {string} fileName - Name of the file to save
 */
export const exportToCSV = (data, columns, fileName = 'report') => {
  const worksheetData = flattenDataForExport(data, columns);
  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileName}_${new Date().getTime()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Flattens data so that if an order has multiple items, it creates multiple rows
 */
const flattenDataForExport = (data, columns) => {
  const flattened = [];
  let serialNumber = 1;

  data.forEach((item) => {
    // If item is already a flat row (e.g. from Stock Overview pre-processing)
    // We check if any of the keys match directly
    const isPreProcessed = columns.some(col => item.hasOwnProperty(col.key));
    if (isPreProcessed && (!item.items || !Array.isArray(item.items))) {
      const row = {};
      columns.forEach(col => {
        if (col.header === 'SN') {
          row[col.header] = serialNumber++;
        } else {
          row[col.header] = item[col.key] || getNestedValue(item, col.key, col.header);
        }
      });
      flattened.push(row);
      return;
    }

    // For Purchase Orders, calculate totals if they're not present (using common field names)
    let poSubtotal = item.itemsTotalPrice;
    let poGrandTotal = item.finalPrice;
    
    // Check if it's a PO based on common PO fields
    if (item.orderId && item.vendorId && item.items) {
      const calculatedSubtotal = item.items.reduce((sum, it) => sum + ((it.orderedQuantity || 0) * (it.rate || 0)), 0);
      if (!poSubtotal) poSubtotal = calculatedSubtotal;
      
      if (!poGrandTotal) {
        const taxPercent = parseFloat(item.tax) || 0;
        poGrandTotal = calculatedSubtotal + (calculatedSubtotal * (taxPercent / 100));
      }
    }

    // Check if item has an 'items' array (e.g. Sales Order items)
    if (item.items && Array.isArray(item.items) && item.items.length > 0) {
      item.items.forEach((subItem, index) => {
        const row = {};
        columns.forEach(col => {
          if (col.header === 'SN') {
            row[col.header] = index === 0 ? serialNumber : '';
          } else if (col.key.startsWith('items.')) {
            // Get value from the sub-item (always show for every item row)
            const subKey = col.key.replace('items.', '');
            row[col.header] = getNestedValue(subItem, subKey, col.header);
          } else {
            // Special handling for PO totals if they're the target column
            if (index === 0) {
              if (col.key === 'itemsTotalPrice') {
                row[col.header] = poSubtotal;
                return;
              }
              if (col.key === 'finalPrice') {
                row[col.header] = poGrandTotal;
                return;
              }
              row[col.header] = getNestedValue(item, col.key, col.header);
            } else {
              row[col.header] = '';
            }
          }
        });
        flattened.push(row);
      });
      serialNumber++;
    } else {
      const row = {};
      columns.forEach(col => {
        if (col.header === 'SN') {
          row[col.header] = serialNumber++;
        } else {
          row[col.header] = getNestedValue(item, col.key, col.header);
        }
      });
      flattened.push(row);
    }
  });

  return flattened;
};

/**
 * Helper to get nested value from object and apply formatting
 * @param {Object} obj 
 * @param {string} path - e.g. 'user.name'
 * @param {string} header - The column header to identify field type
 */
const getNestedValue = (obj, path, header = '') => {
  if (!path) return '';
  const val = path.split('.').reduce((acc, part) => acc && acc[part], obj);
  
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  if (val === undefined || val === null) return '';
  
  // Date formatting based on header or path
  if (header.includes('Date') || header === 'EDD' || path.toLowerCase().includes('date') || path.includes('createdAt')) {
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      
      // If it's an Order Date or similar that needs time
      if (header.includes('Order Date') || path === 'orderDate' || path === 'createdAt') {
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      }
      
      // Default to date only (for EDD etc)
      return `${day}/${month}/${year}`;
    }
  }
  
  return val;
};
